import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { ThemeProvider as MuiThemeProvider, CssBaseline } from "@mui/material"
import { buildTheme } from "../theme"

const ThemeModeContext = createContext(null)

const STORAGE_KEY = "tallywise.theme"

function readStoredMode() {
  try {
    return localStorage.getItem(STORAGE_KEY) || "system"
  } catch {
    // Private windows throw rather than returning null.
    return "system"
  }
}

function systemPrefersDark() {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches
}

export function AppThemeProvider({ children }) {
  const [mode, setMode] = useState(readStoredMode)
  const [systemDark, setSystemDark] = useState(systemPrefersDark)

  // "system" has to keep tracking the OS, not sample it once at mount — otherwise
  // it is just "whatever it was when the tab opened".
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined
    const query = window.matchMedia("(prefers-color-scheme: dark)")
    const onChange = (event) => setSystemDark(event.matches)
    query.addEventListener("change", onChange)
    return () => query.removeEventListener("change", onChange)
  }, [])

  const setThemeMode = useCallback((next) => {
    setMode(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // The choice lasts for this tab. Not worth failing over.
    }
  }, [])

  const resolved = mode === "system" ? (systemDark ? "dark" : "light") : mode
  const theme = useMemo(() => buildTheme(resolved), [resolved])

  const value = useMemo(() => ({ mode, resolvedMode: resolved, setThemeMode }), [mode, resolved, setThemeMode])

  return (
    <ThemeModeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeModeContext.Provider>
  )
}

export function useThemeMode() {
  const ctx = useContext(ThemeModeContext)
  if (!ctx) throw new Error("useThemeMode must be used inside an AppThemeProvider")
  return ctx
}
